import { useState, type ChangeEvent } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { PaletteMode } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { formatNumber } from '@/lib/locale';
import { useUpdateTenantTheme } from '@/hooks/useTenantTheme';
import {
  DEFAULT_CUSTOM_THEME,
  THEME_COLOR_KEYS,
  contrastWarnings,
  isHexColor,
  type CustomTheme,
  type ThemeColorKey,
  type ThemeColors,
} from '@/theme';
import { CustomThemePreview } from './CustomThemePreview';

const MODES: PaletteMode[] = ['light', 'dark'];

/** The colours whose name alone does not say where they show. */
const COLORS_WITH_HELP = new Set<ThemeColorKey>(['primary', 'secondary', 'background']);

function isCompletePalette(colors: ThemeColors): boolean {
  return THEME_COLOR_KEYS.every((key) => isHexColor(colors[key]));
}

/** Hex values compare case-insensitively: the colour picker answers in lower case. */
function isSameTheme(a: CustomTheme, b: CustomTheme): boolean {
  return MODES.every((mode) =>
    THEME_COLOR_KEYS.every((key) => a[mode][key].toLowerCase() === b[mode][key].toLowerCase()));
}

interface ColorFieldProps {
  mode: PaletteMode;
  colorKey: ThemeColorKey;
  value: string;
  onChange: (value: string) => void;
}

/** A hex field with the browser's own colour picker as its adornment; either one sets the value. */
function ColorField({ mode, colorKey, value, onChange }: ColorFieldProps) {
  const { t } = useTranslation();
  const label = t(`settings.customTheme.colors.${colorKey}`);
  const valid = isHexColor(value);
  const help = COLORS_WITH_HELP.has(colorKey) ? t(`settings.customTheme.colorHelp.${mode}.${colorKey}`) : undefined;

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value.trim())}
      error={!valid}
      helperText={valid ? help : t('settings.customTheme.invalid')}
      fullWidth
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="input"
                type="color"
                aria-label={t('settings.customTheme.pickColor', { color: label })}
                value={valid ? value : '#000000'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                sx={{ width: 28, height: 28, p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer' }}
              />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

interface ModeColumnProps {
  mode: PaletteMode;
  colors: ThemeColors;
  onChange: (key: ThemeColorKey, value: string) => void;
}

function ModeColumn({ mode, colors, onChange }: ModeColumnProps) {
  const { t } = useTranslation();
  const complete = isCompletePalette(colors);
  const warnings = complete ? contrastWarnings(colors) : [];

  return (
    <Stack spacing={1} data-testid={`custom-theme-${mode}`}>
      <Typography variant="subtitle1" fontWeight={600}>{t(`settings.customTheme.${mode}`)}</Typography>
      {THEME_COLOR_KEYS.map((key) => (
        <ColorField key={key} mode={mode} colorKey={key} value={colors[key]} onChange={(value) => onChange(key, value)} />
      ))}
      <Typography variant="subtitle2" sx={{ pt: 1 }}>{t('settings.customTheme.preview')}</Typography>
      {complete && <CustomThemePreview mode={mode} colors={colors} />}
      {warnings.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>{t('settings.customTheme.contrastTitle')}</AlertTitle>
          {warnings.map((warning) => (
            <div key={warning.check}>
              {t('settings.customTheme.contrastWarning', {
                pair: t(`settings.customTheme.contrast.${warning.check}`),
                ratio: formatNumber(warning.ratio, { maximumFractionDigits: 1 }),
                minimum: formatNumber(warning.minimum, { maximumFractionDigits: 1 }),
              })}
            </div>
          ))}
        </Alert>
      )}
    </Stack>
  );
}

interface CustomThemeDialogProps {
  /** Where editing starts: the tenant's colours, or the default when it has none yet. */
  initial: CustomTheme;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edits the tenant's own colours, both modes side by side, each with a preview and a warning for
 * text that would be hard to read. Mounted only while open, so it always starts from `initial`.
 */
export function CustomThemeDialog({ initial, onClose, onSaved }: CustomThemeDialogProps) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<CustomTheme>(initial);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const mutation = useUpdateTenantTheme();

  const setColor = (mode: PaletteMode, key: ThemeColorKey, value: string) =>
    setTheme((prev) => ({ ...prev, [mode]: { ...prev[mode], [key]: value } }));

  const save = () => {
    setApiValidationErrors([]);
    mutation.mutate(theme, {
      onSuccess: onSaved,
      onError: (error: unknown) => {
        const errors = getValidationErrors(error);
        setApiValidationErrors(errors.length > 0
          ? errors
          : [{ field: 'form', message: t('common.errorOccurred'), instancePath: '#', members: [] }]);
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('settings.customTheme.dialogTitle')}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.customTheme.dialogHelp')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {MODES.map((mode) => (
            <ModeColumn key={mode} mode={mode} colors={theme[mode]} onChange={(key, value) => setColor(mode, key, value)} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Fills in the default colours; like any edit, they stick once saved. */}
        <Button
          onClick={() => setTheme(DEFAULT_CUSTOM_THEME)}
          disabled={isSameTheme(theme, DEFAULT_CUSTOM_THEME)}
          sx={{ mr: 'auto' }}
        >
          {t('settings.customTheme.reset')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={mutation.isPending || !MODES.every((mode) => isCompletePalette(theme[mode]))}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
